import { FormatDisplay } from "../shared/format";

export type MultiInvoiceType = "adjust" | "replace";

export interface BindInvoiceListOptions {
  statement: any;
  invoiceType: MultiInvoiceType;
  currentInvoice: any;
  taxes?: any[];
  defaultVatCode?: any;
}

export const INVOICE_LIST_REPRESENTATIVE_FIELD =
  "_isInvoiceListRepresentative";

const REPRESENTATIVE_NAME_PATTERN =
  /^(Điều chỉnh|Thay thế) nhiều hoá đơn sai sót tại bảng kê/;

const toSafeAmount = (value: any): number => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const hasAmount = (value: any): boolean =>
  value !== null && value !== undefined && value !== "";

const getStatementItems = (statement: any): any[] => {
  if (Array.isArray(statement?.items)) {
    return statement.items;
  }
  if (Array.isArray(statement?.invoiceListItems)) {
    return statement.invoiceListItems;
  }
  return [];
};

const getAdjustmentAmount = (
  item: any,
  differenceField: string,
  updatedField: string,
  originalField: string,
  fallbackField: string,
) => {
  if (hasAmount(item?.[differenceField])) {
    return toSafeAmount(item[differenceField]);
  }
  if (hasAmount(item?.[updatedField]) || hasAmount(item?.[originalField])) {
    return (
      toSafeAmount(item?.[updatedField]) -
      toSafeAmount(item?.[originalField])
    );
  }
  return toSafeAmount(item?.[fallbackField]);
};

const getReplacementAmount = (
  item: any,
  updatedField: string,
  fallbackField: string,
) =>
  toSafeAmount(
    hasAmount(item?.[updatedField])
      ? item[updatedField]
      : item?.[fallbackField],
  );

export const calculateStatementTotals = (
  statement: any,
  invoiceType: MultiInvoiceType,
) => {
  const items = getStatementItems(statement);
  const calculated = items.reduce(
    (totals, item) => {
      if (invoiceType === "adjust") {
        totals.amountWithoutVAT += getAdjustmentAmount(
          item,
          "beforeTaxAmountDiff",
          "updatedAmountBeforeTax",
          "originalAmountBeforeTax",
          "amountWithoutVAT",
        );
        totals.vatAmount += getAdjustmentAmount(
          item,
          "taxAmountDiff",
          "updatedTax",
          "originalTax",
          "vatAmount",
        );
        totals.totalAmount += getAdjustmentAmount(
          item,
          "amountAfterTaxDiff",
          "updatedAmountAfterTax",
          "originalAmountAfterTax",
          "totalAmount",
        );
        totals.hasAmountWithoutVAT ||= [
          "beforeTaxAmountDiff",
          "updatedAmountBeforeTax",
          "originalAmountBeforeTax",
          "amountWithoutVAT",
        ].some((field) => hasAmount(item?.[field]));
        totals.hasVatAmount ||= [
          "taxAmountDiff",
          "updatedTax",
          "originalTax",
          "vatAmount",
        ].some((field) => hasAmount(item?.[field]));
        totals.hasTotalAmount ||= [
          "amountAfterTaxDiff",
          "updatedAmountAfterTax",
          "originalAmountAfterTax",
          "totalAmount",
        ].some((field) => hasAmount(item?.[field]));
      } else {
        totals.amountWithoutVAT += getReplacementAmount(
          item,
          "updatedAmountBeforeTax",
          "amountWithoutVAT",
        );
        totals.vatAmount += getReplacementAmount(
          item,
          "updatedTax",
          "vatAmount",
        );
        totals.totalAmount += getReplacementAmount(
          item,
          "updatedAmountAfterTax",
          "totalAmount",
        );
        totals.hasAmountWithoutVAT ||= [
          "updatedAmountBeforeTax",
          "amountWithoutVAT",
        ].some((field) => hasAmount(item?.[field]));
        totals.hasVatAmount ||= ["updatedTax", "vatAmount"].some((field) =>
          hasAmount(item?.[field]),
        );
        totals.hasTotalAmount ||= [
          "updatedAmountAfterTax",
          "totalAmount",
        ].some((field) => hasAmount(item?.[field]));
      }
      return totals;
    },
    {
      amountWithoutVAT: 0,
      vatAmount: 0,
      totalAmount: 0,
      hasAmountWithoutVAT: false,
      hasVatAmount: false,
      hasTotalAmount: false,
    },
  );

  const amountWithoutVAT = calculated.hasAmountWithoutVAT
    ? calculated.amountWithoutVAT
    : hasAmount(statement?.amountWithoutVAT)
      ? toSafeAmount(statement.amountWithoutVAT)
      : toSafeAmount(statement?.totalAmountWithoutVAT);

  return {
    amountWithoutVAT,
    totalAmountWithoutVAT: calculated.hasAmountWithoutVAT
      ? calculated.amountWithoutVAT
      : hasAmount(statement?.totalAmountWithoutVAT)
        ? toSafeAmount(statement.totalAmountWithoutVAT)
        : amountWithoutVAT,
    vatAmount: calculated.hasVatAmount
      ? calculated.vatAmount
      : toSafeAmount(statement?.vatAmount),
    totalAmount: calculated.hasTotalAmount
      ? calculated.totalAmount
      : toSafeAmount(statement?.totalAmount),
  };
};

const normalizeVatCode = (value: any): string => {
  const rawValue = `${value ?? ""}`.trim();
  if (rawValue === "-1") {
    return "KCT";
  }
  if (rawValue === "-2") {
    return "KKKNT";
  }
  if (rawValue === "-3") {
    return "KHAC";
  }

  const normalized = rawValue
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/%$/, "")
    .replace(/[\s,._-]+/g, "");

  if (["KCT", "KHONGCHIUTHUE"].includes(normalized)) {
    return "KCT";
  }
  if (
    [
      "KKKNT",
      "KHONGKEKHAI",
      "KHONGKEKHAITINHNOPTHUE",
    ].includes(normalized)
  ) {
    return "KKKNT";
  }
  if (["KHAC", "THUESUATKHAC"].includes(normalized)) {
    return "KHAC";
  }
  return normalized;
};

const hasVatCode = (value: any): boolean =>
  value !== null &&
  value !== undefined &&
  (typeof value === "object" || `${value}`.trim() !== "");

const resolveVatCode = (
  statement: any,
  taxes: any[] = [],
  defaultVatCode: any = "",
) => {
  const firstItem = getStatementItems(statement)[0];
  const rawVatCode = [
    firstItem?.vatCode,
    firstItem?.updatedTaxRate,
    firstItem?.originalTaxRate,
    defaultVatCode,
  ].find(hasVatCode);

  if (rawVatCode && typeof rawVatCode === "object") {
    return rawVatCode;
  }

  const vatCode = `${rawVatCode ?? ""}`.trim();
  if (!vatCode) {
    return "";
  }

  return (
    taxes.find(
      (tax) =>
        [tax?.code, tax?.name, tax?.vatCode].some(
          (value) => normalizeVatCode(value) === normalizeVatCode(vatCode),
        ),
    ) || vatCode
  );
};

const isRepresentativeDetail = (detail: any): boolean =>
  Boolean(detail?.[INVOICE_LIST_REPRESENTATIVE_FIELD]) ||
  REPRESENTATIVE_NAME_PATTERN.test(`${detail?.productName ?? ""}`);

const isBlankDetail = (detail: any): boolean =>
  !`${detail?.productName ?? ""}`.trim();

export const getStatementBuyerInfo = (statement: any) => ({
  buyerLegalName: statement?.buyerLegalName || "",
  buyerDisplayName:
    statement?.buyer || statement?.buyerDisplayName || statement?.buyerName || "",
  buyerTaxCode: statement?.buyerTaxCode || "",
  buyerAddress: statement?.buyerAddress || "",
  buyerCode: statement?.buyerCode || "",
  buyerEmail: statement?.buyerEmail || "",
  buyerBankAccount: statement?.buyerBankAccount || "",
  buyerBankName: statement?.buyerBankName || "",
  buyerTel: statement?.buyerTel || "",
  buyerIdentityCard: statement?.buyerIdentityCard || "",
  passportNumber: statement?.passportNumber || "",
  buyerBudgetUnitCode: statement?.buyerBudgetUnitCode || "",
});

export const bindStatementToInvoice = ({
  statement,
  invoiceType,
  currentInvoice,
  taxes = [],
  defaultVatCode = "",
}: BindInvoiceListOptions) => {
  const source = statement || {};
  const invoice = currentInvoice || {};
  const statementNumber =
    source.statementNumber ?? source.invoiceListNumber ?? "";
  const statementDate = source.statementDate ?? source.invoiceListDate;
  const actionName = invoiceType === "replace" ? "Thay thế" : "Điều chỉnh";
  const formattedDate = statementDate ? FormatDisplay.date(statementDate) : "";
  const totals = calculateStatementTotals(source, invoiceType);

  const currentDetails = Array.isArray(invoice.invoiceDetail)
    ? invoice.invoiceDetail.map((detail) => ({ ...detail }))
    : [];
  let representativeIndex = currentDetails.findIndex(isRepresentativeDetail);

  // Dòng trống được ChiTietHangHoa tự sinh không phải dữ liệu nghiệp vụ; tận dụng
  // dòng này để không giữ lại một dòng thiếu dữ liệu gây lỗi validation.
  if (representativeIndex < 0) {
    representativeIndex = currentDetails.findIndex(isBlankDetail);
  }

  const previousDetail =
    representativeIndex >= 0 ? currentDetails[representativeIndex] : {};
  const representativeDetail = {
    ...previousDetail,
    _id:
      previousDetail?._id ||
      `invoice-list-representative-${source.id || statementNumber || "new"}`,
    [INVOICE_LIST_REPRESENTATIVE_FIELD]: true,
    isShowOrder: previousDetail?.isShowOrder ?? true,
    productName: `${actionName} nhiều hoá đơn sai sót tại bảng kê có Số ${statementNumber} Ngày ${formattedDate}`,
    amount: totals.amountWithoutVAT,
    amountWithoutVAT: totals.amountWithoutVAT,
    vatAmount: totals.vatAmount,
    totalAmount: totals.totalAmount,
    vatCode: resolveVatCode(source, taxes, defaultVatCode),
    property: 1,
    formulaType: "TX",
    ordinalNumber: 1
  };

  if (representativeIndex >= 0) {
    currentDetails[representativeIndex] = representativeDetail;
  } else {
    currentDetails.push(representativeDetail);
  }

  return {
    ...invoice,
    ...getStatementBuyerInfo(source),
    invoiceNote: source.reasons ?? source.reason ?? invoice.invoiceNote ?? "",
    invoiceDetail: currentDetails,
    amount: totals.amountWithoutVAT,
    totalAmountWithoutVAT: totals.amountWithoutVAT,
    vatAmount: totals.vatAmount,
    totalAmount: totals.totalAmount,
  };
};
